import Card from "components/Card/Card";
import { ControlledDateInput } from "components/Card/ControlledInput";
import type { OptionType } from "components/inputs";
import { Checkbox } from "components/inputs";
import {
  getFilteredServiceOptions,
  ServiceMultiSelect,
} from "components/ServiceMultiSelect/ServiceMultiSelect";
import differenceBy from "lodash/differenceBy";
import isNumber from "lodash/isNumber";
import { useCallback, useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import {
  useGetFileByIdQuery,
  usePatchFileByIdMutation,
} from "services/file-api.ts";
import {
  useCreateServiceMutation,
  useDeleteServiceByIdMutation,
} from "services/service-api.ts";
import { useSelector } from "utils/redux";

type ServiceFormData = {
  services: OptionType[];
  is_rush: boolean;
  is_3_day_rush: boolean;
  is_24_hour_rush: boolean;
  is_update_to_prior_msa: boolean;
  due_date: string | Date;
};

const EditServiceForm: React.FC<{
  setShowRRESelection: (value: boolean) => void;
  isReadOnly?: boolean;
}> = ({ setShowRRESelection, isReadOnly = false }) => {
  const { fileId } = useParams<{ fileId: string }>();
  const loggedInUser = useSelector((state) => state.main.loggedInUser);
  const [createService] = useCreateServiceMutation();
  const [deleteServiceById] = useDeleteServiceByIdMutation();
  const [patchFileById] = usePatchFileByIdMutation();

  const {
    control,
    reset,
    register,
    getValues,
    formState: { errors },
  } = useForm<ServiceFormData>();

  const { data: fileData } = useGetFileByIdQuery(
    { id: fileId },
    { skip: !fileId },
  );

  useEffect(() => {
    reset({
      services: fileData?.services || [],
      is_rush: fileData?.is_rush,
      is_3_day_rush: fileData?.is_3_day_rush,
      is_24_hour_rush: fileData?.is_24_hour_rush,
      is_update_to_prior_msa: fileData?.is_update_to_prior_msa ?? false,
      due_date: fileData?.due_date || "",
    });
    if (fileData?.services) {
      const hasSection111 = fileData?.services.some(
        (service) => service.label === "111",
      );
      setShowRRESelection(hasSection111);
    }
  }, [fileData, reset, setShowRRESelection]);

  const handleChangeClientOnFile = useCallback(
    async (field: string, value: unknown) => {
      if (fileId) {
        await patchFileById({
          id: fileId,
          data: {
            [field]: value,
          },
        });
      }
    },
    [patchFileById, fileId],
  );

  const serviceOptions = loggedInUser?.active_licensed_services || [];

  const filteredServiceOptions = useMemo(() => {
    if (!fileData?.services) return serviceOptions;

    return getFilteredServiceOptions(fileData.services, serviceOptions);
  }, [fileData?.services, serviceOptions]);

  const onSave = useCallback(
    async (options: OptionType[]) => {
      const deletedItem = differenceBy(fileData?.services, options, "value");
      const addedItem = options.find((o) => !isNumber(o.value));

      if (addedItem) {
        await createService({
          data: {
            file_id: fileId,
            type: addedItem.value,
          },
        });
        if (addedItem.value === "111") {
          setShowRRESelection(true);
        }
      } else if (deletedItem) {
        // check if deletion should be allowed, extra check after the multi select
        if (fileData?.status && fileData.status !== "U") {
          return; // prevent deletion
        }

        try {
          await deleteServiceById({ id: deletedItem[0]?.id });

          const hasSection111 = options.some(
            (service) => service.label === "111",
          );
          setShowRRESelection(hasSection111);
        } catch (error) {
          console.error("Error deleting service:", error);
        }
      }
    },
    [
      fileData?.services,
      fileData?.status,
      createService,
      fileId,
      setShowRRESelection,
      deleteServiceById,
    ],
  );

  return (
    <Card heading="Service Selection" cardNumber={1}>
      <Controller
        control={control}
        name="services"
        render={({ field: { value, onChange } }) => {
          return (
            <ServiceMultiSelect
              onChange={(options: OptionType[]) => {
                onChange(options);
                if (!isReadOnly) {
                  onSave(options);
                }
              }}
              errors={errors}
              options={filteredServiceOptions}
              value={value}
              fileData={fileData}
              readOnly={isReadOnly}
            />
          );
        }}
      />
      <ControlledDateInput
        classNames="ServiceSelection__DateInput"
        control={control}
        name="due_date"
        label="Due Date"
        errors={errors}
        isClearable={false}
        readOnly={isReadOnly}
        onSave={() =>
          handleChangeClientOnFile("due_date", getValues("due_date"))
        }
        allowFutureDates={true}
      />
      <Checkbox
        {...register("is_rush", {
          onChange: () => {
            if (!isReadOnly) {
              handleChangeClientOnFile("is_rush", getValues("is_rush"));
            }
          },
        })}
        readOnly={isReadOnly}
        label="Rush"
      />
      <Checkbox
        {...register("is_24_hour_rush", {
          onChange: () => {
            if (!isReadOnly) {
              handleChangeClientOnFile(
                "is_24_hour_rush",
                getValues("is_24_hour_rush"),
              );
            }
          },
        })}
        readOnly={isReadOnly}
        label="24 Hour Rush"
      />
      <Checkbox
        {...register("is_3_day_rush", {
          onChange: () => {
            if (!isReadOnly) {
              handleChangeClientOnFile(
                "is_3_day_rush",
                getValues("is_3_day_rush"),
              );
            }
          },
        })}
        readOnly={isReadOnly}
        label="3 Day Rush"
      />
      <Checkbox
        {...register("is_update_to_prior_msa", {
          onChange: () => {
            if (!isReadOnly) {
              handleChangeClientOnFile(
                "is_update_to_prior_msa",
                getValues("is_update_to_prior_msa"),
              );
            }
          },
        })}
        readOnly={isReadOnly}
        label="Is this referral an update to a prior MSA?"
      />
    </Card>
  );
};

export default EditServiceForm;
